import { useEffect, useState, useCallback } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { appConfirm } from "../../../components/ConfirmDialog";
import { Loader2, Plus, Trash2, Save, Lock, Wand2 } from "lucide-react";
import { money } from "./shared";
import { Materials } from "./Materials";

export function CostsTab({ jobKey, role }) {
  const isSenior = role === "senior_admin";
  return (
    <div data-testid="jobfile-costs">
      {isSenior ? (
        <SeniorFinancials jobKey={jobKey} />
      ) : (
        <div className="border border-[#E5E7EB] bg-[#F8F6F2] p-3 mb-6 flex items-center gap-2 text-xs text-[#6B7280]" data-testid="costs-restricted-note">
          <Lock size={13} className="shrink-0" /> £ costs, labour and margin are visible to senior admins only. You can see material quantities below.
        </div>
      )}
      <div className="mt-2">
        <Materials reference={jobKey} />
      </div>
    </div>
  );
}

function SeniorFinancials({ jobKey }) {
  const [fin, setFin] = useState(null);
  const [labour, setLabour] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/job-costs/${encodeURIComponent(jobKey)}/financials`);
      setFin(r.data);
      setLabour((r.data.labour || []).map((l, i) => ({ ...l, _k: `${i}-${l.description}` })));
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load financials"); }
    setLoading(false);
  }, [jobKey]);
  useEffect(() => { load(); }, [load]);

  const setRow = (i, patch) => {
    setLabour(rows => rows.map((r, x) => {
      if (x !== i) return r;
      const next = { ...r, ...patch };
      const d = parseFloat(next.days), rt = parseFloat(next.rate);
      if (("days" in patch || "rate" in patch) && !isNaN(d) && !isNaN(rt)) {
        next.amount = Math.round(d * rt * 100) / 100;
      }
      return next;
    }));
    setDirty(true);
  };
  const addRow = () => { setLabour(rows => [...rows, { description: "", days: "", rate: "", amount: 0, _k: `new-${Date.now()}` }]); setDirty(true); };
  const delRow = (i) => { setLabour(rows => rows.filter((_, x) => x !== i)); setDirty(true); };

  const autoFill = async () => {
    if (labour.length > 0 && !(await appConfirm({
      title: "Replace the current labour lines?",
      description: "Auto-fill suggests a line for every trade this job needs, using the day rates from Settings → Pricing and days estimated from the job size. Nothing is saved until you press Save.",
      confirmLabel: "Auto-fill trades",
    }))) return;
    setAutoBusy(true);
    try {
      const r = await api.post(`/admin/job-costs/${encodeURIComponent(jobKey)}/labour/auto`);
      setLabour((r.data.items || []).map((l, i) => ({ ...l, _k: `auto-${Date.now()}-${i}` })));
      setDirty(true);
      toast.success(`Suggested ${(r.data.items || []).length} trades for ${r.data.floor_area_sqm}m² — adjust & save`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not auto-fill trades"); }
    setAutoBusy(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/job-costs/${encodeURIComponent(jobKey)}/labour`, {
        items: labour.map(({ description, days, rate, amount, trade }) => ({
          description, days: days === "" ? null : days, rate: rate === "" ? null : rate,
          amount: Number(amount) || 0, trade,
        })),
      });
      toast.success("Labour costs saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save labour costs"); }
    setSaving(false);
  };

  if (loading) return <div className="py-6 text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading financials…</div>;
  if (!fin) return null;

  const labourTotal = labour.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const costTotal = (fin.materials_cost || 0) + labourTotal;
  const margin = (fin.sell_price_inc_vat || 0) - costTotal;
  const marginPct = fin.sell_price_inc_vat ? Math.round(margin / fin.sell_price_inc_vat * 1000) / 10 : 0;

  return (
    <div className="mb-6" data-testid="costs-financials">
      <div className="label-eyebrow mb-2">Internal costs &amp; margin <span className="text-[#C0392B]">· senior admin only</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <FinStat label="Sell price" value={money(fin.sell_price_inc_vat)} testId="costs-sell" />
        <FinStat label="Materials (our cost)" value={money(fin.materials_cost)} testId="costs-materials" />
        <FinStat label="Labour" value={money(labourTotal)} testId="costs-labour-total" />
        <FinStat label="Total job expense" value={money(costTotal)} testId="costs-total" />
        <FinStat label={`Margin (${marginPct}%)`} value={money(margin)} highlight={margin >= 0} danger={margin < 0} testId="costs-margin" />
      </div>

      <div className="border border-[#E5E7EB] bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow">Labour costs</div>
          <div className="flex items-center gap-2">
            <button data-testid="costs-labour-auto" onClick={autoFill} disabled={autoBusy}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#5B2A83] border border-[#EADBF3] bg-[#FBF8FD] px-2.5 py-1 hover:border-[#5B2A83] disabled:opacity-50">
              {autoBusy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Auto-fill trades
            </button>
            <button data-testid="costs-labour-add" onClick={addRow}
              className="inline-flex items-center gap-1 text-xs font-bold text-[#12263A] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#C79A3B]">
              <Plus size={12} /> Add line
            </button>
            <button data-testid="costs-labour-save" onClick={save} disabled={!dirty || saving}
              className="inline-flex items-center gap-1 text-xs font-bold bg-[#12263A] text-white px-2.5 py-1 disabled:opacity-40">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        </div>
        {labour.length === 0 && <p className="text-xs text-[#9CA3AF] py-2" data-testid="costs-labour-empty">No labour lines yet — add trades, day rates or fixed prices.</p>}
        {labour.length > 0 && (
          <div className="flex items-center gap-2 pb-1 text-[9px] uppercase tracking-widest font-black text-[#9CA3AF]">
            <span className="flex-1">Trade / description</span>
            <span className="w-16 text-center">Days</span>
            <span className="w-24 text-center">£ / day</span>
            <span className="w-28 text-right pr-1">Total £</span>
            <span className="w-6" />
          </div>
        )}
        {labour.map((l, i) => {
          const hasTimeframe = l.days !== "" && l.days !== null && l.days !== undefined && l.rate !== "" && l.rate !== null && l.rate !== undefined;
          return (
            <div key={l._k} className="flex items-center gap-2 py-1.5 border-t border-[#F0EAE0]" data-testid={`costs-labour-row-${i}`}>
              <input data-testid={`costs-labour-desc-${i}`} value={l.description} placeholder="e.g. Groundworks crew"
                onChange={(e) => setRow(i, { description: e.target.value })}
                className="flex-1 border border-[#E5E7EB] px-2 py-1.5 text-sm min-w-0" />
              <input data-testid={`costs-labour-days-${i}`} type="number" step="0.5" min="0" value={l.days ?? ""} placeholder="—"
                onChange={(e) => setRow(i, { days: e.target.value })}
                className="w-16 border border-[#E5E7EB] px-1.5 py-1.5 text-sm text-center tabular-nums" />
              <input data-testid={`costs-labour-rate-${i}`} type="number" step="5" min="0" value={l.rate ?? ""} placeholder="—"
                onChange={(e) => setRow(i, { rate: e.target.value })}
                className="w-24 border border-[#E5E7EB] px-1.5 py-1.5 text-sm text-center tabular-nums" />
              <input data-testid={`costs-labour-amount-${i}`} type="number" value={l.amount} readOnly={hasTimeframe}
                title={hasTimeframe ? "Auto-calculated from days × rate — clear days or rate to type a fixed price" : "Fixed price"}
                onChange={(e) => setRow(i, { amount: e.target.value })}
                className={`w-28 border px-2 py-1.5 text-sm text-right tabular-nums ${hasTimeframe ? "border-[#F0EAE0] bg-[#F8F6F2] text-[#374151]" : "border-[#E5E7EB]"}`} />
              <button data-testid={`costs-labour-del-${i}`} onClick={() => delRow(i)} className="text-[#C0392B] p-1 hover:bg-[#FEF6F5] w-6 shrink-0"><Trash2 size={14} /></button>
            </div>
          );
        })}
        {labour.length > 0 && (
          <p className="text-[10px] text-[#9CA3AF] mt-2">Change the days (or rate) and the £ total updates automatically. Leave days/rate blank to enter a fixed price instead.</p>
        )}
        {fin.labour_updated_at && (
          <p className="text-[10px] text-[#9CA3AF] mt-2">Last saved {new Date(fin.labour_updated_at).toLocaleString("en-GB")}{fin.labour_updated_by ? ` · ${fin.labour_updated_by}` : ""}</p>
        )}
      </div>
    </div>
  );
}

function FinStat({ label, value, highlight, danger, testId }) {
  return (
    <div data-testid={testId} className={`border p-2.5 ${danger ? "border-[#C0392B] bg-[#FEF6F5]" : highlight ? "border-[#1F6B3B] bg-[#F2F9F2]" : "border-[#E5E7EB] bg-white"}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">{label}</div>
      <div className={`text-base font-black ${danger ? "text-[#C0392B]" : "text-[#12263A]"}`}>{value}</div>
    </div>
  );
}
